import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import type { PeriodPreset } from '@afrilink/ui';
import { sastDateKey, sastMonthKey } from '@/shared/utils/sastDate';

/**
 * useFilterParams — dashboard/report filter state, with the URL as the source
 * of truth.
 *
 * Filters were previously held in `useState`, so a filtered view could not be
 * bookmarked, shared with a manager, or restored by the back button — even
 * though the query-param pattern already existed elsewhere in the codebase
 * (SalesDashboard links to `/sales/quotations?within=2`).
 *
 * WHY THE PERIOD IS RESOLVED HERE AND NOT IN `PeriodFilter`
 * Resolving a preset to a range needs SAST boundaries, and SAST is a business
 * fact, not a design-system one. `@afrilink/ui` stays ignorant of it.
 *
 * WHY THE PERIOD MUST NOT JOIN THE QUERY KEY (architect ruling AR-1.2)
 * Filtering is client-side over one cached result set. Putting the period into
 * `todayScopedKey` would fragment a single cache entry into six for identical
 * data and force a refetch on every chip press. Derive with `useMemo` instead.
 */

/** Inclusive SAST date bounds as `YYYY-MM-DD`; `null` means unbounded. */
export interface PeriodRange {
  start: string | null;
  end: string | null;
}

export const DEFAULT_PERIOD: PeriodPreset = 'this-month';

/** First day of the current SAST quarter, as `YYYY-MM-DD`. */
function sastQuarterStart(at = new Date()): string {
  const key = sastMonthKey(at);                 // YYYY-MM-01
  const year = key.slice(0, 4);
  const month = Number(key.slice(5, 7));
  const firstOfQuarter = Math.floor((month - 1) / 3) * 3 + 1;
  return `${year}-${String(firstOfQuarter).padStart(2, '0')}-01`;
}

/**
 * Calendar arithmetic on a `YYYY-MM-01` key.
 *
 * Deliberately pure string/number maths, matching the approach `sastRecentMonths`
 * already takes: once the SAST month has been resolved there is no timezone
 * left in play, so stepping across a boundary cannot drift. Doing this with
 * `new Date(y, m, 1)` is precisely the §4.14 defect — it is how the reference
 * project's `AdminDateRangeFilter` computes ranges, and how the abandoned 8f.4
 * branch bucketed every invoice issued on the 1st into the previous month.
 */
function addMonths(monthKey: string, delta: number): string {
  const y = Number(monthKey.slice(0, 4));
  const m = Number(monthKey.slice(5, 7));
  const zeroBased = y * 12 + (m - 1) + delta;
  const ny = Math.floor(zeroBased / 12);
  const nm = (zeroBased % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}-01`;
}

/** Days in a calendar month. Leap years by the full Gregorian rule. */
function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/** Last day of the month a `YYYY-MM-01` key names, as `YYYY-MM-DD`. */
function monthEnd(monthKey: string): string {
  const y = Number(monthKey.slice(0, 4));
  const m = Number(monthKey.slice(5, 7));
  return `${monthKey.slice(0, 7)}-${String(daysInMonth(y, m)).padStart(2, '0')}`;
}

/**
 * Turn a preset into inclusive SAST bounds.
 *
 * `end` is left null for the open-ended presets rather than pinned to today —
 * a tab left open overnight would otherwise silently stop counting today's
 * sales at midnight.
 */
export function resolvePeriod(
  preset: PeriodPreset,
  from?: string,
  to?: string,
  at: Date = new Date(),
): PeriodRange {
  switch (preset) {
    case 'this-month':
      return { start: sastMonthKey(at), end: null };
    case 'last-30':
      return { start: sastDateKey(new Date(at.getTime() - 29 * 86_400_000)), end: null };
    case 'this-quarter':
      return { start: sastQuarterStart(at), end: null };
    case 'ytd':
      return { start: `${sastDateKey(at).slice(0, 4)}-01-01`, end: null };
    // CLOSED presets (v2, 2026-09-13). Unlike every case above, these pin `end`.
    // That is the whole point: "show me July's invoices" is a bounded question,
    // and leaving `end` open would silently include August.
    case 'last-month': {
      const start = addMonths(sastMonthKey(at), -1);
      return { start, end: monthEnd(start) };
    }
    case 'last-quarter': {
      const start = addMonths(sastQuarterStart(at), -3);
      return { start, end: monthEnd(addMonths(start, 2)) };
    }
    case 'last-year': {
      const year = Number(sastDateKey(at).slice(0, 4)) - 1;
      return { start: `${year}-01-01`, end: `${year}-12-31` };
    }
    case 'custom':
      return { start: from ?? null, end: to ?? null };
    case 'all-time':
    default:
      return { start: null, end: null };
  }
}

/**
 * Step a CLOSED period one unit backwards or forwards.
 *
 * Returns a resolved `custom` range rather than a new preset, so the URL always
 * states exactly which dates are on screen. An offset vocabulary
 * (`last-month-2`) would need its own resolution rules and would make a shared
 * link ambiguous about when it was created.
 *
 * Returns `null` for an open preset — stepping "year to date" has no meaning,
 * and the caller (PeriodFilter) only offers the arrows for closed presets.
 */
export function stepPeriod(
  preset: PeriodPreset,
  current: PeriodRange,
  direction: -1 | 1,
): { from: string; to: string } | null {
  if (!current.start || !current.end) return null;

  switch (preset) {
    case 'last-month': {
      const start = addMonths(current.start, direction);
      return { from: start, to: monthEnd(start) };
    }
    case 'last-quarter': {
      const start = addMonths(current.start, 3 * direction);
      return { from: start, to: monthEnd(addMonths(start, 2)) };
    }
    case 'last-year': {
      const year = Number(current.start.slice(0, 4)) + direction;
      return { from: `${year}-01-01`, to: `${year}-12-31` };
    }
    default:
      return null;
  }
}

/**
 * Is an event timestamp inside the range?
 *
 * Compares SAST date keys as strings, which is safe because `YYYY-MM-DD` sorts
 * lexically. Converting first is the whole point: a payment at 01:30 SAST on
 * the 1st is 23:30 UTC on the previous month's last day.
 *
 * Only ever call this on an EVENT timestamp (`paid_at`, `created_at`). Applying
 * it to a row set defined by current state turns "open leads" into "leads
 * created in period" — a wrong number that looks right (Rule S-1).
 */
export function inPeriod(iso: string | null | undefined, range: PeriodRange): boolean {
  if (!range.start && !range.end) return true;
  if (!iso) return false;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return false;
  const day = sastDateKey(t);
  if (range.start && day < range.start) return false;
  if (range.end && day > range.end) return false;
  return true;
}

export interface FilterParams {
  period: PeriodPreset;
  from?: string;
  to?: string;
  company?: string;
  sku?: string;
  /** Resolved SAST bounds for the current selection. */
  range: PeriodRange;
  /** Non-default filters applied — drives the bar's summary and Clear. */
  activeCount: number;
  setPeriod: (next: { preset: PeriodPreset; from?: string; to?: string }) => void;
  setCompany: (id?: string) => void;
  setSku: (code?: string) => void;
  clear: () => void;
}

export function useFilterParams(
  opts: {
    defaultPeriod?: PeriodPreset;
    /**
     * Namespace for this filter's URL params, e.g. `'activity'` gives
     * `?activity_period=last-month`.
     *
     * Exists because of AR-4 / Rule S-2: a filter's blast radius is the
     * container it sits in. Two filters on one page scoping different cards
     * must not share `?period`, or moving one silently moves the other and a
     * shared link cannot say which card was scoped. Omit it for the page-level
     * filter, which keeps the short, clean keys.
     */
    paramPrefix?: string;
  } = {},
): FilterParams {
  const defaultPeriod = opts.defaultPeriod ?? DEFAULT_PERIOD;
  const [params, setParams] = useSearchParams();

  const prefix = opts.paramPrefix ? `${opts.paramPrefix}_` : '';
  const K = {
    period: `${prefix}period`,
    from: `${prefix}from`,
    to: `${prefix}to`,
    company: `${prefix}company`,
    sku: `${prefix}sku`,
  };

  const period = (params.get(K.period) as PeriodPreset | null) ?? defaultPeriod;
  const from = params.get(K.from) ?? undefined;
  const to = params.get(K.to) ?? undefined;
  const company = params.get(K.company) ?? undefined;
  const sku = params.get(K.sku) ?? undefined;

  // Recomputed per render rather than memoised on `new Date()`: the bounds are
  // cheap, and memoising them would freeze "this month" for the life of the tab.
  const range = resolvePeriod(period, from, to);

  const write = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          // Defaults are omitted so a clean dashboard link stays clean.
          if (next.get(K.period) === defaultPeriod) next.delete(K.period);
          if (next.get(K.period) !== 'custom') {
            next.delete(K.from);
            next.delete(K.to);
          }
          return next;
        },
        { replace: true },
      );
    },
    // Depends on `paramPrefix`, not on `K` — `K` is derived from it and is
    // rebuilt each render anyway. (No eslint-disable here: this config does not
    // register react-hooks, so the directive would itself be an error — the
    // dangling-directive trap the codebase has hit before.)
    [setParams, defaultPeriod, opts.paramPrefix],
  );

  const setPeriod = useCallback(
    (nextValue: { preset: PeriodPreset; from?: string; to?: string }) =>
      write((next) => {
        next.set(K.period, nextValue.preset);
        if (nextValue.from) next.set(K.from, nextValue.from);
        else next.delete(K.from);
        if (nextValue.to) next.set(K.to, nextValue.to);
        else next.delete(K.to);
      }),
    [write],
  );

  const setCompany = useCallback(
    (id?: string) => write((next) => (id ? next.set(K.company, id) : next.delete(K.company))),
    [write],
  );

  const setSku = useCallback(
    (code?: string) => write((next) => (code ? next.set(K.sku, code) : next.delete(K.sku))),
    [write],
  );

  const clear = useCallback(
    () =>
      write((next) => {
        next.delete(K.period);
        next.delete(K.from);
        next.delete(K.to);
        next.delete(K.company);
        next.delete(K.sku);
      }),
    [write],
  );

  const activeCount = useMemo(
    () => (period !== defaultPeriod ? 1 : 0) + (company ? 1 : 0) + (sku ? 1 : 0),
    [period, defaultPeriod, company, sku],
  );

  return {
    period, from, to, company, sku,
    range, activeCount,
    setPeriod, setCompany, setSku, clear,
  };
}
